import type { TOC } from '@ember/component/template-only';
import Heading from './heading';
import Text from './text';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// SectionHeader – icon + title heading for content sections
// ---------------------------------------------------------------------------

interface SectionHeaderSignature {
  Args: {
    /**
     * Icon component to display
     */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /**
     * Title text
     */
    title: string;
    /**
     * Optional subtitle/description
     */
    subtitle?: string;
    /**
     * Icon color class
     * @default "text-gray-600"
     */
    iconClass?: string;
    /**
     * Icon size
     * @default 20
     */
    iconSize?: number;
    /**
     * Heading level
     * @default 3
     */
    level?: 1 | 2 | 3 | 4;
    /**
     * Bottom margin class
     * @default "mb-4"
     */
    marginClass?: string;
  };
  Blocks: {
    /**
     * Optional content on the right side (e.g., badge, button)
     */
    trailing?: [];
  };
  Element: HTMLDivElement;
}

/**
 * SectionHeader - Icon + title heading for content sections
 *
 * Displays a section header with an icon and title, commonly used
 * at the top of cards and content panels. Supports an optional
 * trailing slot for action buttons or badges.
 *
 * @example Basic usage
 * ```gts
 * import SectionHeader from 'tio-reimagined/components/section-header';
 * import { PhLightbulb } from 'ember-phosphor-icons';
 *
 * <SectionHeader
 *   @icon={{PhLightbulb}}
 *   @title="Quick Tips"
 *   @iconClass="text-amber-500"
 * />
 * ```
 *
 * @example With subtitle
 * ```gts
 * <SectionHeader
 *   @icon={{PhTarget}}
 *   @title="Goals"
 *   @subtitle="Track your progress"
 *   @iconClass="text-violet-600"
 * />
 * ```
 *
 * @example With trailing content
 * ```gts
 * <SectionHeader
 *   @icon={{PhChartLine}}
 *   @title="Statistics"
 *   @iconClass="text-blue-600"
 * >
 *   <:trailing>
 *     <Chip @intent="info" @size="sm">New</Chip>
 *   </:trailing>
 * </SectionHeader>
 * ```
 */
const SectionHeader: TOC<SectionHeaderSignature> = <template>
  <div
    data-test-section-header
    class="flex items-center gap-2 {{if @marginClass @marginClass 'mb-4'}}"
    ...attributes
  >
    <@icon @size={{if @iconSize @iconSize 20}} class={{if @iconClass @iconClass "text-gray-600"}} />
    <div class="flex-1">
      <Heading
        data-test-section-header-title
        @level={{if @level @level 3}}
        class="text-lg font-semibold dark:text-white"
      >
        {{@title}}
      </Heading>
      {{#if @subtitle}}
        <Text data-test-section-header-subtitle @variant="muted" class="text-sm">
          {{@subtitle}}
        </Text>
      {{/if}}
    </div>
    {{#if (has-block "trailing")}}
      <div data-test-section-header-trailing>
        {{yield to="trailing"}}
      </div>
    {{/if}}
  </div>
</template>;

export default SectionHeader;
export type { SectionHeaderSignature };
